import { Helmet } from 'react-helmet-async'
import { useBrand } from '@cannect/hooks/useBrand'

const DEFAULT_IMAGE = 'https://cannect.life/src/assets/img/logo.png'
const BASE_URL = 'https://cannect.life'

const isProductionEnvironment = (): boolean => {
  return import.meta.env.VITE_APP_ENV === 'production'
}

interface SEOProps {
  title: string
  description: string
  canonical?: string
  image?: string
  type?: 'website' | 'article' | 'product'
  noindex?: boolean
  keywords?: string
  author?: string
  publishedTime?: string
}

export const SEO = ({
  title,
  description,
  canonical,
  image,
  type = 'website',
  noindex = false,
  keywords,
  author,
  publishedTime
}: SEOProps) => {
  const { config } = useBrand()
  const siteName = config.name
  const resolvedImage = image ?? config.meta?.favicon ?? DEFAULT_IMAGE
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`
  const url = canonical ? `${BASE_URL}${canonical.startsWith('/') ? canonical : `/${canonical}`}` : undefined
  const truncatedDescription = description.length > 160 ? `${description.substring(0, 157)}...` : description
  const shouldBlockIndexing = noindex || !isProductionEnvironment()

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={truncatedDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      {url && <link rel="canonical" href={url} />}
      {shouldBlockIndexing && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDescription} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:type" content={type} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="pt_BR" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && author && <meta property="article:author" content={author} />}
    </Helmet>
  )
}

export default SEO
